import styles from "./task-template-page.module.css";
import { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import routes from "@/routes";

import dayjs from "dayjs";

import {
  Flex,
  Typography,
  Spin,
  Card,
  App,
  Button,
  Modal,
  Upload,
  Tabs,
  Dropdown,
  ConfigProvider,
} from "antd";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import {
  formatISODateToCustomStr,
  copyLinkToClipboard,
} from "@/utils";

import LockCloseIcon from "@/icons/LockClosedIcon";
import PlusIcon from "@/icons/PlusIcon";
import LinkIcon from "@/icons/LinkIcon";
import InformationIcon from "@/icons/InformationIcon";
import EditIcon from "@/icons/EditIcon";
import ChevronDownIcon from "@/icons/ChevronDownIcon";
import PaperClipIcon from "@/icons/PaperClipIcon";
import CubIcon from "@/icons/CubIcon";
import TrashIcon from "@/icons/TrashIcon";
import DocumentAddIcon from "@/icons/DocumentAddIcon";
import DotsHorizontalIcon from "@/icons/DotsHorizontalIcon";

import TaskModal from "@/components/modals/TaskModal";
import MarkdownText from "@/components/ui/MarkdownText";
import FileModal from "@/components/modals/FileModal";
import AttachingObjectsToTemplateModal from "@/components/modals/AttachingObjectsToTemplateModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import TaskTemplatesModal from "@/components/modals/TaskTemplateModal";
import TaskTemplateInfoModal from "@/components/modals/TaskTemplateInfoModal";
import FileLinkModal from "@/components/modals/FileLinkModal";

import FilesTab from "./FilesTab";
import InfoSection from "./InfoSection";
import ObjectsTab from "./ObjectsTab";
import HistoryTab from "./HistoryTab";
import ChecklistTab from "./ChecklistTab";
import PeriodicalSection from "./PeriodicalSection";
import ActualOnAcceptanceSection from "./ActualOnAcceptanceSection";

import {
  getItem,
  updateItem,
  deleteItem,
  getTaskTemplateFiles,
  getTaskTemplateObjects,
  deleteAttachedObject,
  getChecklist,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  deleteChecklist,
  getUserRoles,
  getRepeatRules,
} from "@/api/task-templates";

import { getImportanceOptions as getPriorities } from "@/api/importances";
import { getStatusOptions as getStatuses } from "@/api/statuses";
import { getTaskTypeOptions as getTaskTypes } from "@/api/taskTypes";
import { getUnitsOptions, getEmployeeUnitsOptions } from "@/api/units";
import { deleteFile } from "@/api/files";
import { getList as getLogs } from "@/api/systemLogs";

import { TEMPLATE_EDIT_ROLES } from "@/constants/task-templates";
// import LaborCostsTab from "./LaborCostsTab";
// import PlannedLaborCostsModal from "@/components/modals/PlannedLaborCostsModal";

const uploadsProps = {
  name: "file",
  action: "/api/file/upload",
  multiple: false,
  showUploadList: false,
};

const getActionMenuItems = ({
  handleAddNewTask,
  isUserCanEditTemplate,
  onUploadFile,
  handleAddFileLink,
  setIsModalObjectsShow,
  handleDelete,
  readOnly,
  isMobile = false,
}) => [
  {
    key: "addTask",
    onClick: handleAddNewTask,
    label: (
      <Flex
        align="center"
        gap={6}
        style={{
          fontSize: 13,
          lineHeight: "21px",
        }}
      >
        <DocumentAddIcon
          style={{
            color: "#BBBBBB",
            fontSize: 16,
          }}
        />
        <span>Создать задачу по шаблону</span>
      </Flex>
    ),
  },
  {
    key: "addFile",
    label: (
      <Upload {...uploadsProps} onChange={onUploadFile}>
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "16px",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <PaperClipIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Добавить вложение</span>
        </Flex>
      </Upload>
    ),
    disabled: !isUserCanEditTemplate,
  },
  {
    key: "addSystemFile",
    onClick: handleAddFileLink,
    label: (
      <Flex
        align="center"
        gap={6}
        style={{
          fontSize: 13,
          lineHeight: "21px",
        }}
      >
        <PaperClipIcon
          style={{
            color: "#BBBBBB",
            fontSize: 16,
          }}
        />
        <span>Добавить вложение из системы</span>
      </Flex>
    ),
    disabled: !isUserCanEditTemplate,
  },
  {
    key: "addObject",
    label: (
      <Flex
        align="center"
        gap={6}
        style={{
          fontSize: 13,
          lineHeight: "21px",
        }}
      >
        <CubIcon
          style={{
            color: "#BBBBBB",
            fontSize: 16,
          }}
        />
        <span>Прикрепить ресурс</span>
      </Flex>
    ),
    onClick: () => setIsModalObjectsShow(true),
  },
  ...(isMobile && !readOnly
    ? [
        {
          type: "divider",
        },
        {
          key: "delete",
          onClick: handleDelete,
          label: (
            <Flex
              align="center"
              gap={6}
              style={{
                fontSize: 13,
                lineHeight: "21px",
              }}
            >
              <TrashIcon
                style={{
                  color: "#BBBBBB",
                  fontSize: 16,
                }}
              />
              <span>Удалить шаблон</span>
            </Flex>
          ),
        },
      ]
    : []),
];

const { Title } = Typography;

const TaskTemplate = ({ isAdmins = false }) => {
  const { id, unit_uuid } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setLayoutPending } = useContext(MainContext);

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [repeatRules, setRepeatRules] = useState({});

  const [activeTab, setActiveTab] = useState("files");

  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [units, setUnits] = useState([]);

  const [status, setStatus] = useState();
  const [priority, setPriority] = useState();
  const [taskType, setTaskType] = useState();

  // Окно загрузки файла из системы
  const [isSystemModalShow, setIsSystemModalShow] = useState(false);

  const is_overdue = useMemo(() => {
    if (!data) return false;
    return data.due_date && new Date() >= new Date(data.due_date);
  }, [data]);

  const [files, setFiles] = useState([]);
  const [objects, setObjects] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({});
  const [checklist, setChecklist] = useState([]);

  const [filesPending, setFilesPending] = useState(true);
  const [objectsPending, setObjectsPending] = useState(true);
  const [logsPending, setLogsPending] = useState(true);
  const [checklistPending, setChecklistPending] = useState(true);
  // const [laborCostsPending, setLaborCostsPending] = useState(false);

  const isExecutor = user?.employee_uuid === data?.executor?.uuid;
  const isAcceptor = user?.employee_uuid === data?.assignee_acceptor?.uuid;

  const [isModalShow, setIsModalShow] = useState(false);
  const [modalItem, setModalItem] = useState();

  const [isModalTaskShow, setIsModalTaskShow] = useState(false);

  const [isModalFileShow, setIsModalFileShow] = useState(false);
  const [currentFile, setCurrentFile] = useState();

  const [isModalInfoShow, setIsModalInfoShow] = useState(false);
  const [isModalObjectsShow, setIsModalObjectsShow] = useState(false);

  // const [isModalEditPlannedLaborCostsShow, setIsModalEditPlannedLaborCostsShow] = useState(false);

  const [userRoles, setUserRoles] = useState([]);
  const isUserCanEditTemplate = useMemo(() => {
    return (
      !data?.readOnly &&
      (isAdmins ||
        userRoles?.some((role) => TEMPLATE_EDIT_ROLES.includes(role)))
    );
  }, [data, isAdmins, userRoles]);

  const showSetMeExecutor = useMemo(() => {
    const access = !isExecutor && isUserCanEditTemplate;
    if (!access) return false;

    if (isUserCanEditTemplate) {
      return access;
    }
  }, [isExecutor, isUserCanEditTemplate]);

  const showSetMeAcceptor = useMemo(() => {
    const access = !isAcceptor && isUserCanEditTemplate;
    if (!access) return false;

    if (isUserCanEditTemplate) {
      return access;
    }
  }, [isAcceptor, isUserCanEditTemplate]);

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const [data, { units }, statuses, priorities, taskTypes, roles, rules] =
        await Promise.all([
          getItem(id, isAdmins),
          isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
          getStatuses(),
          getPriorities(),
          getTaskTypes(),
          getUserRoles(id),
          getRepeatRules(id),
        ]);

      setData(data);
      setStatuses(statuses);
      setPriorities(priorities);
      setTaskTypes(taskTypes);
      setUnits(units);
      setStatus(statuses.find((status) => status.uuid === data.status_uuid));
      setPriority(
        priorities.find((priority) => priority.uuid === data.importance_uuid),
      );
      setTaskType(taskTypes.find((type) => type.uuid === data.task_type_uuid));
      setUserRoles(roles);
      setRepeatRules({
        ...rules,
        ...(rules?.start_date && { start_date: dayjs(rules.start_date) }),
        ...(rules?.end_date && { end_date: dayjs(rules.end_date) }),
      });

      fetchFiles();
      fetchObjects();
      fetchLogs();
      fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFiles = async () => {
    setFilesPending(true);
    try {
      const { files } = await getTaskTemplateFiles({ template_uuid: id });

      setFiles(files);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setFilesPending(false);
    }
  };

  const fetchObjects = async () => {
    setObjectsPending(true);
    try {
      const objects = await getTaskTemplateObjects({ template_uuid: id });

      setObjects(objects);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setObjectsPending(false);
    }
  };

  const fetchLogs = async (page = 1, pageSize) => {
    setLogsPending(true);
    try {
      const params = { entity_id: id, page };

      if (pageSize) {
        params.perPage = pageSize;
      }

      const logs = await getLogs(params, isAdmins);
      setLogs(logs.logs);
      setLogsPagination({
        total: logs.total,
        current: logs.page,
        pageSize: logs.perPage,
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setLogsPending(false);
    }
  };

  const handleHistoryTableChange = (pagination) => {
    fetchLogs(pagination.current, pagination.pageSize);
  };

  const fetchChecklist = async () => {
    setChecklistPending(true);
    try {
      const checklist = await getChecklist(id);
      setChecklist(checklist);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  // После сохранения в модалке шаблона задачи
  const handleSaveItem = (item) => {
    setIsModalShow(false);
    if (item.uuid === data.uuid) fetchData();
    else {
      setActiveTab("files");
    }
  };

  // После сохранения в модалке задачи (может быть подзадачей)
  const handleSaveTaskItem = () => {
    setIsModalTaskShow(false);
    fetchData();
  };

  const setMeExecutor = async () => {
    const res = { ...data };
    res.responsible_executor_uuid = user.employee_uuid;
    setIsLoading(true);

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setMeAcceptor = async () => {
    const res = { ...data };
    res.assignee_acceptor_uuid = user.employee_uuid;
    setIsLoading(true);

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setIsGroupTask = async (isGroupTask) => {
    setIsLoading(true);
    const res = { ...data, is_accessible_for_all_in_unit: isGroupTask };

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик видимости. Отправляет запрос на сервер и обновляет данные
  const handleUpdateVisibleItem = async (value) => {
    setIsLoading(true);
    const res = { ...data, is_visible: value };
    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // Удаление задачи или подзадачи
  const handleDelete = (data) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить шаблон задачи ${data.template_code} ${data.template_title}?`,
      content: "Шаблон будет удален",
      onOk() {
        handleConfirmDelete(data);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем шаблон задачи после подтверждения
  const handleConfirmDelete = async (data) => {
    try {
      await deleteItem({ uuid: data.uuid });

      if (data.uuid === id) {
        isAdmins
          ? navigate(routes.administration.taskTemplates)
          : unit_uuid
            ? navigate(routes.unitTaskTemplates(unit_uuid))
            : navigate(routes.taskTemplates);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Открыть модалку для редактирования текущей задачи
  const handleEditTemplate = () => {
    setModalItem({
      ...data,
      roles: userRoles,
    });
    setIsModalShow(true);
  };

  // Открыть модалку для создания задачи по шаблону
  const handleAddNewTask = () => {
    setModalItem({
      ...data,
    });
    setIsModalTaskShow(true);
  };

  // Открыть модалку для редактирования прикрепленного файла
  const handleEditFile = (item) => {
    setCurrentFile(item);
    setIsModalFileShow(true);
  };

  // После сохранения изменений в файле, закрыть модалку и обновить список файлов
  const onFileModalSave = () => {
    setActiveTab("files");
    setIsModalFileShow(false);
    fetchFiles();
    fetchLogs();
  };

  // Удаление файла
  const handleDeleteFile = (item) => {
    const { name } = item;
    Modal.confirm({
      title: `Вы уверены, что хотите удалить файл ${name} ?`,
      content: "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDeleteFile(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем файл после подтверждения
  const handleConfirmDeleteFile = async ({ uuid }) => {
    setFilesPending(true);
    try {
      await deleteFile({ uuid });

      await fetchFiles();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setFilesPending(false);
    }
  };

  // Обработчик загрузки файла, открывает 2 шаг прикрепления файла
  const onUploadFile = ({ file }) => {
    const { status } = file;

    if (file.status === "uploading") {
      setLayoutPending(true);
      return;
    }

    if (status === "done") {
      setLayoutPending(false);
      const fileObj = file.response.files[0];
      const name = fileObj.original_name.split(".").slice(0, -1).join(".");
      setCurrentFile({
        ...fileObj,
        name,
        folder_uuid: data.folder_uuid || data.template_code,
        unit_uuid: data.unit_uuid,
        folder: {
          uuid: data.template_code,
          label: data.template_code,
        },
        template_uuid: data.uuid,
        additional: true,
        template: true,
        isSystem: false,
      });
      setIsModalFileShow(true);
    } else if (status === "error") {
      setLayoutPending(false);

      message.error(
        `Загрузка файла ${file.name} не удалась. Попробуйте еще раз.`,
      );
    }
  };

  const handleAddFileLink = () => {
    setIsSystemModalShow(true);
  };

  const handleSaveFileLink = async (payload) => {
    const name =
      (payload.name || payload.original_name || "Файл из системы")
        .split(".")
        .slice(0, -1)
        .join(".") ||
      payload.name ||
      payload.original_name ||
      "Файл из системы";

    setCurrentFile({
      ...payload,
      name,
      folder_uuid: data.folder_uuid || data.template_code,
      unit_uuid: data.unit_uuid,
      folder: {
        uuid: data.template_code,
        label: data.template_code,
      },
      template_uuid: data.uuid,
      additional: true,
      template: true,
      isSystem: true,
    });

    setActiveTab("files");
    setIsModalFileShow(true);
    setIsSystemModalShow(false);
  };

  const onAddNewObject = () => {
    setActiveTab("objects");
    setIsModalObjectsShow(false);
    fetchObjects();
    fetchLogs();
  };

  const handleDeleteObject = (data) => {
    Modal.confirm({
      title: `Вы уверены, что хотите открепить ресурс ${data.object.name}?`,
      content:
        "Ресурс будет откреплен от шаблона задачи, но останется в списке ресурсов",
      onOk() {
        handleConfirmDeleteObject(data);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  const handleConfirmDeleteObject = async (data) => {
    setObjectsPending(true);
    try {
      await deleteAttachedObject({
        template_uuid: id,
        object_uuid: data.object.uuid,
      });

      fetchObjects();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setObjectsPending(false);
    }
  };

  // Чеклист
  const handleEditChecklistItem = async (item) => {
    setChecklistPending(true);

    try {
      if (item.uuid) {
        await updateChecklistItem({ ...item, template_uuid: id });
      } else {
        // Новый пункт, добавляем
        await addChecklistItem({ template_uuid: id, title: item.title });
      }
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleDeleteChecklistItem = async (uuid) => {
    setChecklistPending(true);
    try {
      await deleteChecklistItem({ uuid, template_uuid: id });
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleDeleteList = async () => {
    setChecklistPending(true);
    try {
      await deleteChecklist(id);
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  // const handleSavePlannedCosts = async (data) => {
  //   setLaborCostsPending(true);

  //   try {
  //     const item = {
  //       uuid: id,
  //       ...data,
  //     }
  //     await updateItem(item);

  //     fetchData();
  //   } catch (error) {
  //     message.open({
  //       type: "error",
  //       content: error.message,
  //       duration: 10,
  //     });
  //   }

  //   setLaborCostsPending(false);
  //   setIsModalEditPlannedLaborCostsShow(false);
  // }

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <>
            <Flex vertical gap={25}>
              <Flex vertical gap={10}>
                <Breadcrumbs
                  items={
                    isAdmins
                      ? [
                          { title: "Административная панель" },
                          {
                            title: "Задачи",
                          },
                          {
                            title: "Шаблоны задач",
                            href: routes.administration.taskTemplates,
                          },
                          {
                            title: `Шаблон №${data.template_code}`,
                          },
                        ]
                      : [
                          {
                            title: "Главная",
                            href: routes.dashboards,
                          },
                          unit_uuid ? { title: data?.unit?.name } : {},
                          {
                            title: "Задачи",
                          },
                          {
                            title: "Шаблоны задач",
                            href: unit_uuid
                              ? routes.unitTaskTemplates(data.unit_uuid)
                              : routes.taskTemplates,
                          },
                          {
                            title: `Шаблон №${data.template_code}`,
                          },
                        ]
                  }
                />
                <Flex align="flex-start" gap={20} justify="space-between">
                  <Flex align="center" gap={8} wrap="wrap">
                    <Title level={2} className="no-margin">
                      {data.template_title}
                    </Title>
                  </Flex>

                  <Flex gap={8}>
                    {data.readOnly && (
                      <LockCloseIcon
                        style={{ color: "#BBBBBB", fontSize: 18 }}
                      />
                    )}
                    <Flex gap={4}>
                      {!data.readOnly && (
                        <>
                          <Button
                            color="default"
                            variant="text"
                            type="text"
                            disabled={!isUserCanEditTemplate}
                            onClick={handleEditTemplate}
                            icon={<EditIcon />}
                            style={{ padding: 0 }}
                            className="hide-desktop"
                          />

                          <Button
                            color="default"
                            variant="text"
                            type="text"
                            icon={<LinkIcon />}
                            onClick={async () => {
                              const result = await copyLinkToClipboard(
                                window.location.href,
                              );
                              message.open({
                                type: result.success ? "success" : "error",
                                content: result.message,
                                duration: 5,
                              });
                            }}
                            style={{ padding: 0 }}
                          />
                        </>
                      )}

                      <div className="hide-desktop">
                        <Dropdown
                          menu={{
                            items: getActionMenuItems({
                              handleAddNewTask,
                              isUserCanEditTemplate,
                              onUploadFile,
                              handleAddFileLink,
                              setIsModalObjectsShow,
                              handleDelete: () => handleDelete(data),
                              readOnly: data.readOnly,
                              isMobile: true,
                            }),
                          }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <Button
                            color="default"
                            variant="text"
                            type="text"
                            icon={<DotsHorizontalIcon />}
                          />
                        </Dropdown>
                      </div>
                    </Flex>

                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: "#E66A25",
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<InformationIcon />}
                        onClick={() => setIsModalInfoShow(true)}
                        className="hide-desktop"
                      />
                    </ConfigProvider>
                  </Flex>
                </Flex>
              </Flex>

              <Flex gap={20}>
                <Flex gap={30} vertical className={styles.body}>
                  <Flex gap={10} vertical>
                    <Card>
                      <Flex vertical gap={30} justify="space-between">
                        <Flex vertical gap={15}>
                          <div style={{ color: "#777777" }}>
                            {`Создано ${formatISODateToCustomStr(
                              data.created_at,
                            )}, обновлено ${formatISODateToCustomStr(
                              data.updated_at,
                            )}`}
                          </div>
                          <Title
                            level={4}
                            style={{ margin: 0, fontSize: 15, fontWeight: 500 }}
                          >
                            {data.template_title}
                          </Title>

                          {data.description ? (
                            <MarkdownText value={data.description} />
                          ) : isUserCanEditTemplate ? (
                            <div
                              className={styles.fakeDescription}
                              onClick={handleEditTemplate}
                            >
                              <PlusIcon style={{ color: "#BBBBBB" }} />
                              Добавить описание
                            </div>
                          ) : (
                            <div className={styles.fakeDescriptionLock}>
                              Описание
                            </div>
                          )}
                        </Flex>
                      </Flex>
                    </Card>

                    {data.require_actuals_on_acceptance && (
                      <div className="hide-desktop">
                        <ActualOnAcceptanceSection />
                      </div>
                    )}

                    {data.is_recurring_template && (
                      <div className="hide-desktop">
                        <PeriodicalSection repeatRules={repeatRules} />
                      </div>
                    )}
                  </Flex>

                  <Tabs
                    activeKey={activeTab}
                    items={[
                      {
                        key: "files",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Вложения</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {files?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <FilesTab
                            data={files}
                            pending={filesPending}
                            unit_uuid={data.unit_uuid}
                            handleAddNew={onUploadFile}
                            handleEditFile={handleEditFile}
                            handleDeleteFile={handleDeleteFile}
                            handleAddFileLink={handleAddFileLink}
                            readOnly={!isUserCanEditTemplate}
                            uploadsProps={uploadsProps}
                          />
                        ),
                      },
                      {
                        key: "objects",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Связанные ресурсы</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {objects?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <ObjectsTab
                            data={objects}
                            pending={objectsPending}
                            isAdmins={isAdmins}
                            handleAddNew={() => setIsModalObjectsShow(true)}
                            handleDelete={handleDeleteObject}
                          />
                        ),
                      },
                      {
                        key: "checklist",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Чек-лист</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {checklist?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <ChecklistTab
                            data={checklist}
                            pending={checklistPending}
                            readOnly={!isUserCanEditTemplate}
                            handleEditItem={handleEditChecklistItem}
                            handleDeleteItem={handleDeleteChecklistItem}
                            handleDeleteList={handleDeleteList}
                          />
                        ),
                      },
                      // {
                      //   key: "labelCosts",
                      //   label: "Трудозатраты",
                      //   children: (
                      //     <LaborCostsTab
                      //       planned={Number(data.planned_costs)}
                      //       pending={laborCostsPending}
                      //       canEditPlanned={isUserCanEditTemplate}
                      //       handleEditPlannedLaborCosts={() => setIsModalEditPlannedLaborCostsShow(true)}
                      //     />
                      //   ),
                      // },
                      // Всегда последняя вкладка
                      {
                        key: "history",
                        label: "История изменений",
                        children: (
                          <HistoryTab
                            data={logs}
                            pending={logsPending}
                            pagination={logsPagination}
                            onTableChange={handleHistoryTableChange}
                          />
                        ),
                      },
                    ]}
                    onTabClick={(key) => setActiveTab(key)}
                  />
                </Flex>

                <div className={styles.aside}>
                  <Flex
                    vertical
                    gap={10}
                    style={{ position: "sticky", top: 0 }}
                  >
                    <Card>
                      <InfoSection
                        data={data}
                        priority={priority}
                        taskType={taskType}
                        is_overdue={is_overdue}
                        isUserCanEditTemplate={isUserCanEditTemplate}
                        handleEditTask={handleEditTemplate}
                        showSetMeExecutor={showSetMeExecutor}
                        showSetMeAcceptor={showSetMeAcceptor}
                        setMeExecutor={setMeExecutor}
                        setMeAcceptor={setMeAcceptor}
                        setIsGroupTask={setIsGroupTask}
                        handleUpdateVisibleItem={handleUpdateVisibleItem}
                      />
                    </Card>
                    {data.require_actuals_on_acceptance && (
                      <ActualOnAcceptanceSection />
                    )}

                    {data.is_recurring_template && (
                      <PeriodicalSection repeatRules={repeatRules} />
                    )}
                    <Card>
                      <Flex justify="space-between" align="center">
                        <Flex gap={6}>
                          <Button
                            disabled={!isUserCanEditTemplate}
                            onClick={handleEditTemplate}
                            icon={<EditIcon style={{ fontSize: 14 }} />}
                          >
                            Редактировать
                          </Button>

                          <Dropdown
                            menu={{
                              items: getActionMenuItems({
                                handleAddNewTask,
                                isUserCanEditTemplate,
                                onUploadFile,
                                handleAddFileLink,
                                setIsModalObjectsShow,
                                handleDelete: () => handleDelete(data),
                                readOnly: data.readOnly,
                              }),
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Button>
                              Действия
                              <ChevronDownIcon style={{ fontSize: 14 }} />
                            </Button>
                          </Dropdown>
                        </Flex>

                        <Button
                          disabled={!isUserCanEditTemplate}
                          onClick={() => handleDelete(data)}
                          icon={<TrashIcon style={{ fontSize: 14 }} />}
                        ></Button>
                      </Flex>
                    </Card>
                  </Flex>
                </div>
              </Flex>
            </Flex>

            {isModalShow && (
              <TaskTemplatesModal
                isAdmins={isAdmins}
                data={modalItem}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                units={units}
                visible={isModalShow}
                onSave={handleSaveItem}
                onCancel={() => setIsModalShow(false)}
                unit_uuid={unit_uuid}
              />
            )}

            {isModalTaskShow && (
              <TaskModal
                isAdmins={isAdmins}
                data={null}
                template={modalItem}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                units={units}
                visible={isModalTaskShow}
                onSave={handleSaveTaskItem}
                onCancel={() => setIsModalTaskShow(false)}
                unit_uuid={unit_uuid}
              />
            )}

            {isModalFileShow && (
              <FileModal
                data={currentFile}
                visible={isModalFileShow}
                units={units}
                disableUnit
                disableFolder
                onSave={onFileModalSave}
                onCancel={() => setIsModalFileShow(false)}
              />
            )}

            {isModalInfoShow && (
              <TaskTemplateInfoModal
                data={data}
                readOnly={data.readOnly}
                status={status}
                priority={priority}
                taskType={taskType}
                is_overdue={is_overdue}
                isUserCanEditTemplate={isUserCanEditTemplate}
                showSetMeExecutor={showSetMeExecutor}
                showSetMeAcceptor={showSetMeAcceptor}
                setMeExecutor={setMeExecutor}
                setMeAcceptor={setMeAcceptor}
                setIsGroupTask={setIsGroupTask}
                visible={isModalInfoShow}
                onOk={() => {
                  setIsModalInfoShow(false);
                  handleEditTemplate();
                }}
                onCancel={() => setIsModalInfoShow(false)}
              />
            )}

            {isModalObjectsShow && (
              <AttachingObjectsToTemplateModal
                unit_uuid={unit_uuid}
                template_uuid={id}
                visible={isModalObjectsShow}
                onSave={onAddNewObject}
                onCancel={() => setIsModalObjectsShow(false)}
              />
            )}

            {isSystemModalShow && (
              <FileLinkModal
                visible={isSystemModalShow}
                onCancel={() => setIsSystemModalShow(false)}
                unit_uuid={unit_uuid}
                onSave={handleSaveFileLink}
              />
            )}

            {/* {isModalEditPlannedLaborCostsShow && (
              <PlannedLaborCostsModal
                data={data}
                visible={isModalEditPlannedLaborCostsShow}
                onCancel={() => setIsModalEditPlannedLaborCostsShow(false)}
                onSave={handleSavePlannedCosts}
              />
            )} */}
          </>
        )
      )}
    </>
  );
};

export default TaskTemplate;
